import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ArrowLeft, CheckCircle, Star, Lightning, Crown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const SubscriptionManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [features, setFeatures] = useState(null);
  const [billingCycle, setBillingCycle] = useState('MONTHLY');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pricingRes, orgsRes] = await Promise.all([
        api.get('/subscription-pricing'),
        api.get('/organizations')
      ]);
      setPricing(pricingRes.data.pricing);
      setFeatures(pricingRes.data.features);
      setOrganizations(orgsRes.data);
    } catch {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (orgId, plan) => {
    try {
      await api.patch(`/organizations/${orgId}/subscription?status=ACTIVE&plan=${plan}&billing_cycle=${billingCycle}`);
      toast.success(`Subscription updated to ${plan}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  if (loading || !pricing || !features) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0096FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </button>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>
            Subscription Plans
          </h1>
          <p className="text-[#9E9E9E] text-lg">Choose the right plan for your show production needs</p>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center gap-1 bg-[#161616] rounded-lg p-1 w-fit mx-auto mb-8" data-testid="billing-toggle">
          {[
            { value: 'MONTHLY', label: 'Monthly' },
            { value: 'SEMIANNUAL', label: '6-Month', badge: '10% off' },
            { value: 'ANNUAL', label: 'Yearly', badge: '20% off' }
          ].map(({ value, label, badge }) => (
            <button
              key={value}
              onClick={() => setBillingCycle(value)}
              data-testid={`billing-${value.toLowerCase()}`}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
                billingCycle === value
                  ? 'bg-[#0096FF] text-white'
                  : 'text-[#9E9E9E] hover:text-white'
              }`}
            >
              {label}
              {badge && billingCycle !== value && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#34C759]/15 text-[#34C759] rounded-full font-bold">{badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Standard */}
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 relative" data-testid="plan-standard">
            <div className="flex items-center gap-2 mb-3">
              <Star size={22} weight="fill" className="text-[#0096FF]" />
              <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Standard</h2>
            </div>
            <div className="mb-4">
              <span className="text-4xl font-black text-white" style={{fontFamily: 'Outfit'}}>
                ${pricing.STANDARD[billingCycle].price}
              </span>
              <span className="text-[#9E9E9E] text-sm">/mo</span>
              {billingCycle !== 'MONTHLY' && (
                <p className="text-[#34C759] text-xs mt-1">
                  ${pricing.STANDARD[billingCycle].total} billed {billingCycle === 'SEMIANNUAL' ? 'every 6 months' : 'annually'} — save {pricing.STANDARD[billingCycle].savings}
                </p>
              )}
            </div>
            <p className="text-[#9E9E9E] text-sm mb-4">Perfect for small show producers running single-day events</p>
            <div className="space-y-2 mb-6">
              {features.STANDARD.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" className="text-[#0096FF] flex-shrink-0" />
                  <span className="text-[#E0E0E0]">{f}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm opacity-40">
                <CheckCircle size={16} className="text-[#555] flex-shrink-0" />
                <span className="text-[#555] line-through">Multi-day events</span>
              </div>
              <div className="flex items-center gap-2 text-sm opacity-40">
                <CheckCircle size={16} className="text-[#555] flex-shrink-0" />
                <span className="text-[#555] line-through">Stall & RV management</span>
              </div>
            </div>
            {organizations.length > 0 && (
              <button
                onClick={() => handleSubscribe(organizations[0].id, 'STANDARD')}
                data-testid="subscribe-standard"
                className="w-full py-3 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded-lg font-bold text-sm hover:bg-[#0096FF]/25 transition-colors"
              >
                {organizations[0].subscription_plan === 'STANDARD' ? 'Current Plan' : 'Switch to Standard'}
              </button>
            )}
          </div>

          {/* Professional */}
          <div className="bg-[#161616] border-2 border-[#FF9500] rounded-xl p-6 relative" data-testid="plan-professional">
            <div className="absolute -top-3 right-4 px-3 py-1 bg-[#FF9500] text-black text-xs font-black rounded-full">MOST POPULAR</div>
            <div className="flex items-center gap-2 mb-3">
              <Crown size={22} weight="fill" className="text-[#FF9500]" />
              <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit'}}>Professional</h2>
            </div>
            <div className="mb-4">
              <span className="text-4xl font-black text-white" style={{fontFamily: 'Outfit'}}>
                ${pricing.PROFESSIONAL[billingCycle].price}
              </span>
              <span className="text-[#9E9E9E] text-sm">/mo</span>
              {billingCycle !== 'MONTHLY' && (
                <p className="text-[#34C759] text-xs mt-1">
                  ${pricing.PROFESSIONAL[billingCycle].total} billed {billingCycle === 'SEMIANNUAL' ? 'every 6 months' : 'annually'} — save {pricing.PROFESSIONAL[billingCycle].savings}
                </p>
              )}
            </div>
            <p className="text-[#9E9E9E] text-sm mb-4">For producers running multi-day events with full accommodations</p>
            <div className="space-y-2 mb-6">
              {features.PROFESSIONAL.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" className="text-[#FF9500] flex-shrink-0" />
                  <span className="text-[#E0E0E0]">{f}</span>
                </div>
              ))}
            </div>
            {organizations.length > 0 && (
              <button
                onClick={() => handleSubscribe(organizations[0].id, 'PROFESSIONAL')}
                data-testid="subscribe-professional"
                className="w-full py-3 bg-[#FF9500] text-black rounded-lg font-bold text-sm hover:bg-[#FF9500]/80 transition-colors"
              >
                {organizations[0].subscription_plan === 'PROFESSIONAL' ? 'Current Plan' : 'Upgrade to Professional'}
              </button>
            )}
          </div>
        </div>

        {/* Platform fee note */}
        <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 text-center">
          <p className="text-[#9E9E9E] text-sm">
            <Lightning size={14} weight="fill" className="inline text-[#FACC15] mr-1" />
            A service fee plus payment processing is added to all rider entry checkouts. This goes to The Show Master platform — producers receive the full entry fees.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
